import React, { useContext, useState } from 'react';
import * as yup from 'yup';
import { ApiContext } from '@api/api';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { Dialog } from 'primereact/dialog';
import useForm from '@hooks/useForm/useForm';
import dayjs, { Dayjs } from 'dayjs';
import { Button } from 'primereact/button';
import { apiPragueFromDayjsLocal, apiUtcFromDayjs } from '@utils/dateWithTz.tsx';
import { RingilH2 } from '@components/styles.tsx';
import HorizontalFieldValue from '@components/obsolete/HorizontalFieldValue.tsx';
import InputDeadline, { DeadlineMode, DeadlineUnit } from '@components/RingilComponents/CarrierDistributionSelector/InputDeadline.tsx';
import ButtonLayout from '@components/obsolete/Form/ButtonLayout/ButtonLayout.tsx';

interface ComponentProps {
  onCompleted: () => void;
  utcDateTime: Dayjs;
  timezoneId: string;
  groupDemandId: string;
}

interface FormData {
  deadlineMode: DeadlineMode;
  offsetValue: number;
  offsetUnit: DeadlineUnit;
  utcDateTime: Dayjs;
  timezoneId: string;
}
export const useCreateChangeValidTillDialog = () => {
  const [visible, setVisible] = useState(false);

  const CreateChangeValidTillDialog: React.FC<ComponentProps> = ({ groupDemandId, onCompleted, utcDateTime, timezoneId }) => {
    const { tr } = useTranslationLgs();
    const { cuChangeGroupDemandDistributionValidTill } = useContext(ApiContext);

    const form = useForm<FormData>(
      yup.object().shape({}),
      {
        deadlineMode: 'relative',
        offsetValue: 4,
        offsetUnit: 'hours',
        utcDateTime,
        timezoneId,
      },
      data => save(data),
      true,
      false,
    );

    const resolveDeadline = (data: FormData) => {
      const tz = data.timezoneId || 'Europe/Prague';
      if (data.deadlineMode === 'relative') {
        const offsetHours = data.offsetUnit === 'days' ? data.offsetValue * 24 : data.offsetValue;
        return { localTime: dayjs().add(offsetHours, 'hour'), timezone: tz };
      }
      return { localTime: data.utcDateTime, timezone: tz };
    };

    const save = (data: FormData) => {
      const resolved = resolveDeadline(data);
      cuChangeGroupDemandDistributionValidTill(
        groupDemandId,
        {
          validTill: apiPragueFromDayjsLocal(resolved.localTime),
          utcEnd: apiUtcFromDayjs(resolved.localTime),
          timezoneId: resolved.timezone,
        },
        () => {
          setVisible(false);
          onCompleted();
        },
      );
    };
    return (
      <>
        {/*
        // @ts-ignore*/}
        <Dialog onHide={() => setVisible(false)} visible={visible}>
          <RingilH2>Poptat dopravce</RingilH2>
          <HorizontalFieldValue
            label={tr(`ChangeValidTillDialog.untilWhenDoUouNeedAPriceOffer`, `Until when do you need a price offer`)}
            value={
              <InputDeadline
                mode={form.values.deadlineMode}
                offsetValue={form.values.offsetValue}
                offsetUnit={form.values.offsetUnit}
                absoluteLocalTime={form.values.utcDateTime}
                absoluteTimezone={form.values.timezoneId}
                onChangeRelative={(value, unit) =>
                  form.useProduce(draft => {
                    draft.offsetValue = value;
                    draft.offsetUnit = unit;
                  })
                }
                onChangeAbsolute={(localDate, tz) =>
                  form.useProduce(draft => {
                    draft.utcDateTime = localDate;
                    if (tz) draft.timezoneId = tz;
                    draft.deadlineMode = 'absolute';
                  })
                }
                onModeChange={mode => form.setFieldValue('deadlineMode', mode)}
                preserveAbsoluteOnFirstFlip
              />
            }
          />
          <ButtonLayout wrapper="WITH_PADDING_TOP_0">
            <Button label={tr(`CreateChangeValidTillDialog.confirm`, `Potvrdit`)} className="p-mr-3" onClick={() => form.validateAndSend()} />
            <Button label={tr(`CreateChangeValidTillDialog.cancel`, `Zrušit`)} className="p-button-outlined p-button-text" onClick={() => setVisible(false)} />
          </ButtonLayout>
        </Dialog>
      </>
    );
  };

  return { CreateChangeValidTillDialog, setVisible };
};
